import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/apiService';
import PageHeader from '../components/ui/PageHeader';
import {
  WrenchScrewdriverIcon,
  UsersIcon,
  MegaphoneIcon,
  PuzzlePieceIcon,
} from '@heroicons/react/24/outline';

/* ---------- module + form schema config ---------- */

type Field = {
  key: string;
  label: string;
  type?: 'text' | 'textarea' | 'number' | 'select' | 'datetime' | 'date' | 'boolean' | 'list' | 'wards';
  options?: string[];
  required?: boolean;
  hint?: string;
};

// Grouped for a left-side subnav instead of a 13-item tab bar that wrapped
// into two crammed, ungrouped rows with no visual hierarchy.
const MODULE_GROUPS: { label: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>>; items: [string, string][] }[] = [
  {
    label: 'Field Operations',
    icon: WrenchScrewdriverIcon,
    items: [
      ['assets', 'Infrastructure Assets'],
      ['work-orders', 'Work Orders'],
      ['sla-policies', 'SLA Policies'],
      ['checklists', 'Field Checklists'],
      ['outages', 'Service Outages'],
    ],
  },
  {
    label: 'Workforce & Suppliers',
    icon: UsersIcon,
    items: [
      ['technician-profiles', 'Technician Capabilities'],
      ['technician-shifts', 'Shifts'],
      ['contractors', 'Contractors'],
      ['costs', 'Costs'],
    ],
  },
  {
    label: 'Public & Comms',
    icon: MegaphoneIcon,
    items: [
      ['knowledge', 'Knowledge Base'],
      ['templates', 'Message Templates'],
      ['campaigns', 'Campaigns'],
    ],
  },
  {
    label: 'Integrations',
    icon: PuzzlePieceIcon,
    items: [['integrations', 'Integrations']],
  },
];

const MODULES: [string, string][] = MODULE_GROUPS.flatMap((g) => g.items);

/** Guided create-forms per module (replaces the old raw-JSON fallback). */
const SCHEMAS: Record<string, Field[]> = {
  assets: [
    { key: 'asset_number', label: 'Asset number', required: true },
    { key: 'name', label: 'Name', required: true },
    { key: 'type', label: 'Type', required: true, hint: 'e.g. pump_station, streetlight, hydrant' },
    { key: 'status', label: 'Status', type: 'select', options: ['operational', 'degraded', 'failed', 'maintenance'] },
    { key: 'address', label: 'Address' },
    { key: 'ward_number', label: 'Ward', type: 'number' },
    { key: 'installed_on', label: 'Installed on', type: 'date' },
    { key: 'next_maintenance_on', label: 'Next maintenance', type: 'date' },
  ],
  contractors: [
    { key: 'name', label: 'Company name', required: true },
    { key: 'registration_number', label: 'Registration number' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'capabilities', label: 'Capabilities', type: 'list', hint: 'Comma-separated, e.g. plumbing, electrical' },
    { key: 'status', label: 'Status', type: 'select', options: ['active', 'suspended', 'terminated'] },
  ],
  'work-orders': [
    { key: 'work_order_number', label: 'Work order number', required: true },
    { key: 'title', label: 'Title', required: true },
    { key: 'scope', label: 'Scope of work', type: 'textarea', required: true },
    { key: 'contractor_id', label: 'Contractor ID', hint: 'Copy from the Contractors list' },
    { key: 'report_id', label: 'Linked report ID' },
    { key: 'status', label: 'Status', type: 'select', options: ['draft', 'issued', 'accepted', 'in_progress', 'completed', 'cancelled'] },
    { key: 'due_at', label: 'Due', type: 'datetime' },
  ],
  'sla-policies': [
    { key: 'name', label: 'Policy name', required: true },
    { key: 'priority', label: 'Priority', type: 'select', options: ['low', 'normal', 'high', 'urgent'] },
    { key: 'category_id', label: 'Category ID', hint: 'Leave blank for all categories' },
    { key: 'acknowledgement_minutes', label: 'Acknowledge within (minutes)', type: 'number', required: true },
    { key: 'resolution_minutes', label: 'Resolve within (minutes)', type: 'number', required: true },
    { key: 'is_active', label: 'Active', type: 'boolean' },
  ],
  'technician-profiles': [
    { key: 'user_id', label: 'Technician user ID', required: true },
    { key: 'skills', label: 'Skills', type: 'list', hint: 'Comma-separated' },
    { key: 'certifications', label: 'Certifications', type: 'list' },
    { key: 'max_concurrent_jobs', label: 'Max concurrent jobs', type: 'number' },
    { key: 'availability', label: 'Availability', type: 'select', options: ['available', 'busy', 'off_shift', 'on_leave'] },
  ],
  checklists: [
    { key: 'name', label: 'Checklist name', required: true },
    { key: 'category_id', label: 'Category ID' },
    { key: 'items', label: 'Checklist items', type: 'list', required: true, hint: 'Comma-separated steps' },
    { key: 'active', label: 'Active', type: 'boolean' },
  ],
  outages: [
    { key: 'title', label: 'Title', required: true },
    { key: 'description', label: 'Description', type: 'textarea', required: true },
    { key: 'service_type', label: 'Service', type: 'select', options: ['water', 'electricity', 'roads', 'waste', 'other'], required: true },
    { key: 'status', label: 'Status', type: 'select', options: ['planned', 'active', 'resolved'] },
    { key: 'affected_wards', label: 'Affected wards', type: 'wards',
      hint: 'Citizens only see this outage on their dashboard if their ward is listed here — leave empty and it will only appear in the general Alerts list, not on any ward-specific card.' },
    { key: 'starts_at', label: 'Starts', type: 'datetime', required: true },
    { key: 'estimated_end_at', label: 'Estimated end', type: 'datetime' },
    { key: 'public', label: 'Visible to the public', type: 'boolean' },
  ],
  knowledge: [
    { key: 'title', label: 'Title', required: true },
    { key: 'slug', label: 'Slug', required: true, hint: 'lowercase-with-dashes' },
    { key: 'summary', label: 'Summary' },
    { key: 'content', label: 'Content', type: 'textarea', required: true },
    { key: 'audience', label: 'Audience', type: 'select', options: ['public', 'internal'] },
    { key: 'status', label: 'Status', type: 'select', options: ['draft', 'published', 'archived'] },
  ],
  costs: [
    { key: 'report_id', label: 'Report ID' },
    { key: 'work_order_id', label: 'Work order ID' },
    { key: 'cost_type', label: 'Cost type', type: 'select', options: ['labour', 'materials', 'equipment', 'contractor', 'other'], required: true },
    { key: 'description', label: 'Description', required: true },
    { key: 'total_cost', label: 'Total cost (R)', type: 'number', required: true },
  ],
  templates: [
    { key: 'name', label: 'Template name', required: true },
    { key: 'channel', label: 'Channel', type: 'select', options: ['sms', 'push', 'email'], required: true },
    { key: 'subject', label: 'Subject' },
    { key: 'body', label: 'Body', type: 'textarea', required: true },
    { key: 'requires_approval', label: 'Requires approval', type: 'boolean' },
    { key: 'active', label: 'Active', type: 'boolean' },
  ],
  campaigns: [
    { key: 'name', label: 'Campaign name', required: true },
    { key: 'channel', label: 'Channel', type: 'select', options: ['sms', 'push'], required: true },
    { key: 'body', label: 'Message', type: 'textarea', required: true },
    { key: 'audience', label: 'Audience', hint: 'e.g. all, ward:4' },
    { key: 'scheduled_at', label: 'Schedule for', type: 'datetime' },
  ],
  integrations: [
    { key: 'name', label: 'Integration name', required: true },
    { key: 'type', label: 'Type', hint: 'e.g. webhook, gis, billing' },
    { key: 'base_url', label: 'Base URL', required: true },
    { key: 'status', label: 'Status', type: 'select', options: ['inactive', 'active', 'error'] },
  ],
};

/* ---------- wards chip picker ----------
 * No wards master-list exists anywhere in the schema (ward numbers are just
 * a plain integer scattered across reports/assets), so this can't be a
 * validated dropdown — it's a numeric chip input instead of the old raw
 * "type comma-separated numbers" text field, which was easy to fat-finger
 * (stray spaces, non-numeric junk) and gave no visual confirmation of what
 * had actually been entered. */
const WardsChipInput: React.FC<{ value: number[]; onChange: (wards: number[]) => void }> = ({ value, onChange }) => {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const n = Number(draft.trim());
    if (draft.trim() !== '' && Number.isInteger(n) && n > 0 && !value.includes(n)) {
      onChange([...value, n].sort((a, b) => a - b));
    }
    setDraft('');
  };

  return (
    <div className="mt-1">
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {value.map((w) => (
          <span key={w}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border border-primary-300 bg-primary-50 text-primary-800 dark:border-primary-700 dark:bg-primary-900/40 dark:text-primary-200">
            Ward {w}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== w))}
              className="hover:text-red-600" aria-label={`Remove ward ${w}`}>×</button>
          </span>
        ))}
        {value.length === 0 && (
          <span className="text-xs text-gray-400">No wards added — will only show in the general Alerts list.</span>
        )}
      </div>
      <div className="flex gap-2">
        <input
          type="number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); }
          }}
          placeholder="Ward number"
          className="input-field w-32"
        />
        <button type="button" onClick={commit} className="btn-secondary">Add</button>
      </div>
    </div>
  );
};

/* ---------- guided form ---------- */

const GuidedForm: React.FC<{
  fields: Field[];
  onSubmit: (payload: Record<string, any>) => Promise<void>;
}> = ({ fields, onSubmit }) => {
  const [values, setValues] = useState<Record<string, any>>({});
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: any) => setValues((prev) => ({ ...prev, [k]: v }));
  const valid = fields.every((f) => !f.required || String(values[f.key] ?? '').trim() !== '');

  const submit = async () => {
    setBusy(true);
    try {
      const payload: Record<string, any> = {};
      for (const f of fields) {
        const raw = values[f.key];
        if (raw === undefined || raw === '') continue;
        if (f.type === 'number') payload[f.key] = Number(raw);
        else if (f.type === 'boolean') payload[f.key] = Boolean(raw);
        else if (f.type === 'list') {
          payload[f.key] = JSON.stringify(
            String(raw).split(',').map((s) => s.trim()).filter(Boolean)
              .map((s) => (/^\d+$/.test(s) ? Number(s) : s))
          );
        } else if (f.type === 'wards') {
          // `raw` is already a number[] built by the chip picker below.
          payload[f.key] = JSON.stringify(Array.isArray(raw) ? raw : []);
        } else if (f.type === 'datetime' || f.type === 'date') {
          payload[f.key] = new Date(raw).toISOString();
        } else payload[f.key] = raw;
      }
      await onSubmit(payload);
      setValues({});
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {fields.map((f) => (
        <label key={f.key} className="block text-sm">
          <span className="font-medium text-gray-700 dark:text-gray-300">
            {f.label}{f.required && <span className="text-red-500"> *</span>}
          </span>
          {f.type === 'textarea' ? (
            <textarea rows={4} value={values[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)}
              className="input-field mt-1" />
          ) : f.type === 'select' ? (
            <select value={values[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)} className="input-field mt-1 capitalize">
              <option value="">Choose…</option>
              {f.options!.map((o) => <option key={o} value={o}>{o.replaceAll('_', ' ')}</option>)}
            </select>
          ) : f.type === 'boolean' ? (
            <div className="mt-1">
              <input type="checkbox" checked={Boolean(values[f.key])} onChange={(e) => set(f.key, e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary-700 focus:ring-primary-500" />
            </div>
          ) : f.type === 'wards' ? (
            <WardsChipInput value={values[f.key] ?? []} onChange={(wards) => set(f.key, wards)} />
          ) : (
            <input
              type={f.type === 'number' ? 'number' : f.type === 'datetime' ? 'datetime-local' : f.type === 'date' ? 'date' : 'text'}
              value={values[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)} className="input-field mt-1" />
          )}
          {f.hint && <span className="text-xs text-gray-400">{f.hint}</span>}
        </label>
      ))}
      <button onClick={submit} disabled={busy || !valid} className="btn-primary w-full">
        {busy ? 'Creating…' : 'Create record'}
      </button>
    </div>
  );
};

/* ---------- page ---------- */

const ServiceDeliveryHub: React.FC = () => {
  const [active, setActive] = useState('assets');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [query, setQuery] = useState('');
  const [analytics, setAnalytics] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r: any = await apiService.get(`/platform/${active}`);
      setRows(r.data || []);
    } catch (e: any) {
      setError(e.response?.data?.error?.message || 'Unable to load module');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [active]); // reload when the module changes
  useEffect(() => {
    apiService.get('/platform/analytics/production').then((r: any) => setAnalytics(r.data)).catch(() => {});
  }, []);

  const create = async (payload: Record<string, any>) => {
    try {
      await apiService.post(`/platform/${active}`, payload);
      setNotice('Record created.');
      window.setTimeout(() => setNotice(''), 3000);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.error?.message || 'Unable to create record');
    }
  };

  const label = (r: any) => r.name || r.title || r.asset_number || r.work_order_number || r.description || r.user_id || r.id;
  const filtered = useMemo(
    () => rows.filter((r) => !query.trim() || String(label(r)).toLowerCase().includes(query.trim().toLowerCase())),
    [rows, query]
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Service Delivery Operations Hub"
        subtitle="Manage field operations, infrastructure, suppliers, public information and integrations."
      />

      {analytics && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div className="rounded-sm border border-gray-300 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 shadow-sm">
            <p className="text-xs text-gray-500 dark:text-gray-400">Recorded cost</p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">R {Number(analytics.totalCost || 0).toLocaleString()}</p>
          </div>
          <div className="rounded-sm border border-gray-300 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 shadow-sm">
            <p className="text-xs text-gray-500 dark:text-gray-400">Report states</p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{analytics.reports?.length || 0}</p>
          </div>
          <div className="rounded-sm border border-gray-300 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 shadow-sm">
            <p className="text-xs text-gray-500 dark:text-gray-400">SLA states</p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{analytics.sla?.length || 0}</p>
          </div>
          <div className="rounded-sm border border-gray-300 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 shadow-sm">
            <p className="text-xs text-gray-500 dark:text-gray-400">Work-order states</p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{analytics.workOrders?.length || 0}</p>
          </div>
        </div>
      )}

      {error && <div role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {notice && <div role="status" className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">{notice}</div>}

      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        {/* Module subnav */}
        <nav aria-label="Service delivery modules" className="space-y-4">
          {MODULE_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="mb-1.5 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                <group.icon className="h-3.5 w-3.5" />
                {group.label}
              </div>
              <div className="space-y-0.5">
                {group.items.map(([key, name]) => {
                  const isActive = active === key;
                  return (
                    <button
                      key={key}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActive(key)}
                      className={`relative flex w-full items-center rounded-md px-2.5 py-1.5 text-left text-sm transition-colors ${
                        isActive ? 'bg-primary-50 font-medium text-primary-800' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                      }`}
                    >
                      {isActive && <span aria-hidden className="absolute inset-y-1 left-0 w-0.5 rounded-full bg-primary-700" />}
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Module content */}
        <div className="grid gap-5 xl:grid-cols-3">
          <div className="space-y-3 xl:col-span-2">
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter records…"
              aria-label="Filter records" className="input-field max-w-sm text-sm" />
            <div className="overflow-auto rounded-sm border border-gray-300 bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50 text-left">
                  <th className="p-3 font-medium text-gray-500 dark:text-gray-400">Name / title</th><th className="p-3 font-medium text-gray-500 dark:text-gray-400">Status</th><th className="p-3 font-medium text-gray-500 dark:text-gray-400">Updated</th>
                </tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td className="p-4 text-gray-500 dark:text-gray-400" colSpan={3}>Loading…</td></tr>
                  ) : filtered.map((r) => (
                    <tr key={r.id || r.user_id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="p-3">{label(r)}</td>
                      <td className="p-3 capitalize">{String(r.status || r.availability || ((r.is_active ?? r.active) ? 'active' : 'inactive')).replaceAll('_', ' ')}</td>
                      <td className="p-3 text-gray-500 dark:text-gray-400">{r.updated_at ? new Date(r.updated_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                  {!loading && !filtered.length && <tr><td className="p-4 text-gray-500 dark:text-gray-400" colSpan={3}>No records yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="h-fit rounded-sm border border-gray-300 bg-white p-5 dark:border-gray-700 dark:bg-gray-800 shadow-sm">
            <h2 className="mb-3 font-semibold text-gray-900 dark:text-gray-100">New {MODULES.find(([k]) => k === active)?.[1].replace(/s$/, '')}</h2>
            {SCHEMAS[active] ? (
              <GuidedForm fields={SCHEMAS[active]} onSubmit={create} />
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">This module is read-only here for now.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDeliveryHub;
